import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { AuthLayout, Button, Input, PasskeyLoginButton } from "@shop-ledger/ui-components";
import { usePlatformAuth } from "../auth/PlatformAuthContext";
import { PLATFORM_API_MODE } from "../env";

/**
 * Step 1 of §9.1's mandatory two-step login: email + password only. On success this
 * never yields a session directly — `PlatformAuthContext.requestLogin` moves `status` to
 * `"awaiting-mfa"`, and `App.tsx`'s routing sends the operator to `/mfa` next. There is no
 * "skip MFA" path anywhere in this component. In real mode (the default), there is no
 * demo account — an operator must be provisioned first via
 * `app/scripts/seed_platform_operator.py` (see ARCHITECTURE.md's "Running it locally").
 */
export function LoginPage() {
  const { status, requestLogin, loginWithPasskey, loginPending, loginError } = usePlatformAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  if (status === "awaiting-mfa") return <Navigate to="/mfa" replace />;
  if (status === "authenticated") return <Navigate to="/" replace />;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await requestLogin(email, password);
    } catch {
      // loginError is already set by PlatformAuthContext; nothing further to do here.
    }
  }

  return (
    <AuthLayout
      eyebrow="Platform operator"
      title="Operator sign-in"
      subtitle="MFA is required for every login."
      tagline="The console behind every tenant on the platform — provisioning, suspension, and the numbers that don't belong to any one ledger."
      onSubmit={handleSubmit}
    >
      {PLATFORM_API_MODE === "mock" ? (
        <div className="demo-credentials-hint">
          <strong>Demo credentials</strong> — email <code>operator@shopledger.dev</code>, password{" "}
          <code>platform-demo</code>. This mock accepts exactly two seeded operator accounts; see
          ARCHITECTURE.md for the other one.
        </div>
      ) : (
        <div className="demo-credentials-hint">
          There is no self-signup for platform operators. If you don't have credentials, contact your platform
          administrator.
        </div>
      )}
      <Input
        label="Email"
        type="email"
        autoComplete="username"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <Input
        label="Password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />
      {loginError ? (
        <p className="banner banner--error" role="alert">
          {loginError}
        </p>
      ) : null}
      <Button type="submit" variant="primary" size="md" disabled={loginPending} style={{ width: "100%" }}>
        {loginPending ? "Checking…" : "Continue"}
      </Button>
      {/* A real WebAuthn ceremony has no honest mock-mode equivalent (see
          platformWebauthnClient.ts's doc comment) — offered only against the real backend. */}
      {PLATFORM_API_MODE !== "mock" ? (
        <PasskeyLoginButton onLoginWithPasskey={loginWithPasskey} disabled={loginPending} />
      ) : null}
    </AuthLayout>
  );
}
